import React, { useEffect, useRef } from 'react';
import { analytics } from 'lib/analytics';
import { jwtDecode } from 'jwt-decode';
import {
  useFreeTrialContext,
  useFreeTrialPageNavigation,
} from 'providers/FreeTrialProvider';

import {
  usePostCreateMutation,
  SHARED_POST_CREATE_CACHE_KEY,
} from 'services/freeTrial';
import { Progress } from 'features/freeTrial';

export default function SiteCreating() {
  useHandleRedirection();
  return <Progress delay message="We're getting things ready for you" />;
}

// ------------------------------------------------------------- //
// Redirection
// ------------------------------------------------------------- //
function useHandleRedirection() {
  const { actions, meta } = useFreeTrialContext();
  const navigateToPage = useFreeTrialPageNavigation();
  const hasRedirected = useRef(false);

  // Share cache from BasicInfoForm page's handle submit
  // disable no-unused-vars because we don't need the post call
  // eslint-disable-next-line no-unused-vars
  const [_, { isSuccess, isError, error, data }] = usePostCreateMutation({
    fixedCacheKey: SHARED_POST_CREATE_CACHE_KEY,
  });

  // Pull out the message and token from the response
  // And assign to variables of string value to be used in the useEffect
  const message = data?.message ?? '';
  const token = data?.token ?? '';

  const cleanup = () => {
    hasRedirected.current = false;
  };

  // Helper method for handling success responses
  // Error is handled below in the useEffect directly because it's simpler
  const handleSuccessResponse = (responseMessage, userToken) => {
    const responseHandler = {
      'User already exists': () =>
        navigateToPage(meta.pages.ERROR_USER_ALREADY_EXISTS),
      'Site already created': () =>
        navigateToPage(meta.pages.ERROR_SITE_ALREADY_CREATED),
      default: () => {
        // Set token to auto-login user
        actions.setUserToken(userToken);
        navigateToPage(meta.pages.SITE_READY);
      },
    };

    // Find a matching message or fall back to the default handler
    const handler =
      Object.keys(responseHandler).find((key) =>
        responseMessage.includes(key)
      ) || 'default';

    responseHandler[handler]();
  };

  useEffect(() => {
    // Guard against multiple redirects
    if (hasRedirected.current) {
      return cleanup();
    }

    if (isError) {
      hasRedirected.current = true;
      console.log(`%cBasic Info Progress Error Response`, 'color: red');
      console.log(error);
      if (error?.data?.detail?.includes('angle brackets')) {
        navigateToPage(meta.pages.ERROR_ANGLE_BRACKETS);
      } else {
        navigateToPage(meta.pages.ERROR_DEFAULT);
      }
    } else if (isSuccess) {
      hasRedirected.current = true;

      // Set token and identify user
      analytics.init();
      analytics.user.setToken(token);
      analytics.user.identify({ forceIdentify: true });

      // Track site ready now
      const { site } = jwtDecode(token);
      analytics.freeTrial.trackSiteReady(site);
      handleSuccessResponse(message, token);
    }

    return cleanup();
  }, [isSuccess, isError, message, token]);
}
